import { useTranslation } from "react-i18next";

import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { BrandWordmark } from "./BrandWordmark";
import { APP_URL } from "./siteUrls";

/**
 * The brand mark. Exported so the sign-in pages wear the same one — they share
 * this surface's visual language, and a second hand-drawn copy would drift.
 */
export function LogoMark() {
  return (
    <svg width="26" height="26" viewBox="0 0 26 26" fill="none" aria-hidden="true">
      <defs>
        <linearGradient id="lm" x1="0" y1="0" x2="26" y2="26" gradientUnits="userSpaceOnUse">
          <stop stopColor="#2779A7" />
          <stop offset="1" stopColor="#49C5B6" />
        </linearGradient>
      </defs>
      <line x1="13" y1="13" x2="4" y2="5" stroke="#94a3b8" strokeWidth="1.2" />
      <line x1="13" y1="13" x2="22" y2="6" stroke="#94a3b8" strokeWidth="1.2" />
      <line x1="13" y1="13" x2="6" y2="21" stroke="#94a3b8" strokeWidth="1.2" />
      <line x1="13" y1="13" x2="21" y2="20" stroke="#94a3b8" strokeWidth="1.2" />
      <circle cx="4" cy="5" r="2.4" fill="#DF6C4F" />
      <circle cx="22" cy="6" r="2.4" fill="#2779A7" />
      <circle cx="6" cy="21" r="2.4" fill="#2779A7" />
      <circle cx="21" cy="20" r="2.4" fill="#DF6C4F" />
      <circle cx="13" cy="13" r="4.4" fill="url(#lm)" />
    </svg>
  );
}

/** Dropdown caret. Shared so the invisible spacer sizes exactly like the trigger. */
function Caret({ className = "" }: { className?: string }) {
  return (
    <svg
      className={`h-3 w-3 ${className}`}
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path d="M3 4.5 6 7.5 9 4.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * The Delgentic apex navbar. It carries NO auth CTA at all: nothing on this origin does OIDC
 * (see catalog/CatalogApp.tsx), so a "Sign in" button here could only ever mean "leave for
 * Scheda", which is not what a nav button should promise. The way into the product is the
 * products menu's "Try out Scheda" item, plus the hero and footer CTAs.
 */
export function Navbar() {
  const { t } = useTranslation();
  // Every item here must scroll to a section this page actually renders
  // (see DelgenticLanding). An FAQ item used to sit at the end pointing at
  // "#faq", which no section on the apex owns, so it landed the visitor on the
  // footer's call to action instead of any answers. There is no apex FAQ yet.
  const links = [
    { label: t("nav.how"), href: "#how" },
    { label: t("nav.agents"), href: "#card" },
    { label: t("nav.contact"), href: "#contact" },
  ];

  return (
    <header className="fixed inset-x-0 top-0 z-50 flex justify-center px-4 pt-4">
      <nav className="nav-capsule relative flex w-full max-w-5xl items-center justify-between rounded-full px-3 py-2">
        {/* "/" not "#top": from a legal page (/privacy, /cookies) the brand must
            navigate HOME, not just scroll the current page to its top. */}
        <a href="/" className="flex items-center gap-2 pl-1 pr-2">
          <LogoMark />
          <BrandWordmark />
        </a>

        <div className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-1 md:flex">
          {links.map((l) =>
            l.href === "#how" ? (
              // "How it works" also opens the products menu — one product today
              // (Scheda), but the markup is a list so more slot in later.
              //
              // The trigger and the items live inside ONE rounded container, so the
              // glow outlines their combined shape rather than drawing a separate
              // floating card. That container is absolutely positioned over an
              // invisible spacer, so opening it grows the outline downward without
              // shifting the navbar.
              <div key={l.href} className="group relative">
                <span
                  aria-hidden="true"
                  className="invisible inline-flex items-center gap-1 whitespace-nowrap px-3 py-1.5 text-sm"
                >
                  {l.label}
                  <Caret />
                </span>

                <div className="absolute left-1/2 top-0 z-50 min-w-full -translate-x-1/2 rounded-2xl border border-transparent transition duration-150 ease-out group-hover:border-[color:var(--accent2)] group-hover:bg-white/90 group-hover:shadow-[0_0_22px_-4px_rgba(6,182,212,0.55)] group-hover:backdrop-blur group-focus-within:border-[color:var(--accent2)] group-focus-within:bg-white/90 group-focus-within:shadow-[0_0_22px_-4px_rgba(6,182,212,0.55)]">
                  <a
                    href={l.href}
                    aria-haspopup="true"
                    className="flex items-center justify-center gap-1 whitespace-nowrap rounded-2xl px-3 py-1.5 text-sm text-[color:var(--body)] transition hover:text-[color:var(--ink)]"
                  >
                    {l.label}
                    <Caret className="transition-transform duration-150 ease-out group-hover:rotate-180" />
                  </a>

                  {/* grid-rows 0fr → 1fr animates the height without a magic pixel value. */}
                  <div className="grid grid-rows-[0fr] transition-[grid-template-rows] duration-100 ease-out group-hover:grid-rows-[1fr] group-focus-within:grid-rows-[1fr]">
                    <div className="overflow-hidden">
                      <div className="mx-2 border-t border-[color:var(--line2)]" />
                      <a
                        href={APP_URL}
                        className="flex items-center justify-between gap-3 whitespace-nowrap rounded-xl px-3 py-2 text-sm font-medium transition hover:bg-[color:var(--bg2)]"
                      >
                        <span className="ink-accent">{t("nav.tryScheda")}</span>
                        <svg
                          className="h-3.5 w-3.5 text-[color:var(--accent2)] transition-transform duration-150 ease-out group-hover:translate-x-0.5"
                          viewBox="0 0 14 14"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          aria-hidden="true"
                        >
                          <path
                            d="M3 7h8M7.5 3.5 11 7l-3.5 3.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <a
                key={l.href}
                href={l.href}
                className="rounded-full px-3 py-1.5 text-sm text-[color:var(--body)] transition hover:bg-[color:var(--bg2)] hover:text-[color:var(--ink)]"
              >
                {l.label}
              </a>
            ),
          )}
        </div>

        <div className="flex items-center gap-1.5">
          <LanguageSwitcher />
        </div>
      </nav>
    </header>
  );
}
